import { getApiBaseUrl } from '../config/env';

const CACHE_KEY = 'jobscribe_public_plans_v3';
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes — keep short so admin plan changes reflect quickly

/** @type {Promise<import('./publicPlans').PublicPlansPayload> | null} */
let inflightRequest = null;

function normalizePublicPlansPayload(data = {}) {
  const plans = Array.isArray(data.plans) ? data.plans : [];
  const annualSavingsPercent = Number(data.annualSavingsPercent) || 0;
  const annualSavingsPercentMin = Number(data.annualSavingsPercentMin) || 0;
  const hasVariableAnnualSavings = Boolean(data.hasVariableAnnualSavings);

  return {
    plans,
    annualSavingsPercent,
    annualSavingsPercentMin,
    hasVariableAnnualSavings,
  };
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt || !Array.isArray(parsed.plans)) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return normalizePublicPlansPayload(parsed);
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        fetchedAt: Date.now(),
        ...payload,
      })
    );
  } catch {
    // Quota or private mode — ignore
  }
}

/** Clear cached public plans (e.g. after admin pricing changes in same browser). */
export function clearPublicPlansCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
  inflightRequest = null;
}

/**
 * Synchronous read of fresh cached plans (for instant first paint).
 * @returns {import('./publicPlans').PublicPlansPayload | null}
 */
export function getCachedPublicPlans() {
  return readCache();
}

/**
 * Fetch active subscription plans (deduped, session-cached).
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<import('./publicPlans').PublicPlansPayload>}
 */
export async function fetchPublicPlans(options = {}) {
  const { forceRefresh = false } = options;

  if (!forceRefresh) {
    const cached = readCache();
    if (cached) return cached;
  }

  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    const res = await fetch(`${getApiBaseUrl()}/public/plans`, {
      credentials: 'omit',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });
    if (!res.ok) {
      throw new Error('Failed to fetch pricing plans');
    }
    const json = await res.json();
    const data = json.data ?? json;
    const payload = normalizePublicPlansPayload(data);
    writeCache(payload);
    return payload;
  })();

  try {
    return await inflightRequest;
  } finally {
    inflightRequest = null;
  }
}

/**
 * Warm the cache early (call on app / landing mount).
 */
export function prefetchPublicPlans() {
  const cached = readCache();
  if (cached) return;
  void fetchPublicPlans().catch(() => {});
}
